import {
  CommandId,
  type EnvironmentId,
  type PlanApprovalMode,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { useEffect } from "react";
import { readEnvironmentApi } from "../../environmentApi";

// Edit-plan flow is not yet implemented (no composer-repopulation plumbing exists).
// The button is visually disabled; this callback is intentionally a no-op so that
// triggering it never silently discards the proposed plan.
function onEditPlan() {
  // no-op: real edit flow requires composer-repopulation plumbing not yet built
}

async function respondToPlanApproval(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId;
  readonly promptId: string;
  readonly decision: "approve" | "discard";
}): Promise<void> {
  const api = readEnvironmentApi(input.environmentId);
  if (!api) return;
  await api.orchestration.dispatchCommand({
    type: "thread.plan-approval.respond",
    commandId: CommandId.make(
      `plan-approval:${input.activeThreadId}:${input.promptId}:${input.decision}`,
    ),
    threadId: input.activeThreadId,
    promptId: input.promptId,
    decision: input.decision,
    createdAt: new Date().toISOString(),
  });
}

export function usePlanApprovalControls(input: {
  readonly environmentId: EnvironmentId;
  readonly activeThreadId: ThreadId | null;
  readonly promptId: string | null;
  readonly planApprovalMode: PlanApprovalMode;
  readonly workerSpawnMode: "require" | "autoAfterPlan" | "autoEverywhere";
}): {
  readonly onApprovePlan: () => void;
  readonly onDiscardPlan: () => void;
  readonly onEditPlan: () => void;
} {
  const shouldAutoApprove =
    input.planApprovalMode !== "require" && input.workerSpawnMode !== "require";
  useEffect(() => {
    if (!shouldAutoApprove || input.activeThreadId === null || input.promptId === null) return;
    void respondToPlanApproval({
      environmentId: input.environmentId,
      activeThreadId: input.activeThreadId,
      promptId: input.promptId,
      decision: "approve",
    });
  }, [
    input.activeThreadId,
    input.environmentId,
    input.promptId,
    input.planApprovalMode,
    input.workerSpawnMode,
    shouldAutoApprove,
  ]);

  function onRespondToPlanApproval(decision: "approve" | "discard") {
    if (input.activeThreadId === null || input.promptId === null) return;
    void respondToPlanApproval({
      environmentId: input.environmentId,
      activeThreadId: input.activeThreadId,
      promptId: input.promptId,
      decision,
    });
  }

  const onApprovePlan = () => void onRespondToPlanApproval("approve");
  const onDiscardPlan = () => void onRespondToPlanApproval("discard");

  return { onApprovePlan, onDiscardPlan, onEditPlan };
}
